"use client";
import { useEffect, useState, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { API_BASE } from "@/lib/api";
import { getSettings } from "@/lib/credits";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  Typography,
  Avatar,
  Tooltip,
  IconButton,
  Button,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";

type Model = {
  id: string;
  label: string;
  color?: string;
  badge?: string;
  logo?: string;
};

export default function NavBar() {
  const router = useRouter();
  const [models, setModels] = useState<Model[]>([]);
  const [model, setModel] = useState<string>("auto");
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [usageTotals, setUsageTotals] = useState<{
    prompt: number;
    completion: number;
    total: number;
  } | null>(null);
  const [usageLast, setUsageLast] = useState<{
    prompt: number;
    completion: number;
    total: number;
    at: number;
  } | null>(null);
  const [activeConv, setActiveConv] = useState<string | null>(null);
  const [credit, setCredit] = useState<number>(0);
  const [lowThreshold, setLowThreshold] = useState<number>(100);
  const [creditReady, setCreditReady] = useState<boolean>(false);
  const [thresholdReady, setThresholdReady] = useState<boolean>(false);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackMsg, setSnackMsg] = useState<string>("");
  const [snackSeverity, setSnackSeverity] = useState<
    "success" | "info" | "warning" | "error"
  >("info");
  const lastLevelRef = useRef<"none" | "warn" | "out">("none");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Provider logos (fallbacks to colored badge if undefined)
  const providerLogo: Record<string, string> = {
    openai: "/logo/gpt.jpg",
    google: "/logo/gemini.png",
    deepseek: "/logo/deepseek.png",
    auto: "/img/10633768.png",
    meta: "/logo/meta.jpg",
    perplexity: "/logo/sonar.png",
    anthropic: "/logo/claude.png",
  };

  // Load models from backend
  useEffect(() => {
    async function loadModels() {
      let res: Response;
      try {
        res = await fetch(`${API_BASE}/chat/models`, {
          credentials: "include",
        });
      } catch {
        return;
      }
      if (!res.ok) return;
      const data = await res.json().catch(() => null);
      if (!data || typeof data !== "object" || Array.isArray(data)) return;

      // แปลง object ที่ backend ส่งให้เป็น flat array
      const flat: Model[] = Object.entries(data as Record<string, any>).flatMap(
        ([provider, entries]) =>
          Object.entries(entries as Record<string, any>)
            // Hide deepseek-code/coder variants
            .filter(([key, id]) => {
              const k = String(key).toLowerCase();
              const v = String(id).toLowerCase();
              if (
                provider === "deepseek" &&
                (/^code(r)?$/.test(k) || /(deepseek)[-\/](code|coder)/.test(v))
              )
                return false;
              return true;
            })
            .map(([key, id]) => ({
              id: String(id),
              label: `${provider} (${key})`,
              color:
                provider === "openai"
                  ? "#10b981"
                  : provider === "google"
                  ? "#22c55e"
                  : provider === "anthropic"
                  ? "#f59e0b"
                  : provider === "deepseek"
                  ? "#0ea5e9"
                  : provider === "meta"
                  ? "#374151"
                  : provider === "perplexity"
                  ? "#64748b"
                  : "#6b7280",
              badge: provider.slice(0, 2).toUpperCase(),
              logo: providerLogo[provider] || undefined,
            }))
      );

      // กัน id ซ้ำ (บาง provider/key อาจ map ไปยัง id เดียวกัน) ซึ่งทำให้ key ของ MenuItem ซ้ำ
      const seenIds = new Set<string>(["auto"]);
      const dedupedFlat = flat.filter((m) => {
        if (seenIds.has(m.id)) return false;
        seenIds.add(m.id);
        return true;
      });

      setModels([
        {
          id: "auto",
          label: "Auto Router",
          color: "#F59E0B",
          badge: "AR",
          logo: providerLogo["auto"],
        },
        ...dedupedFlat,
      ]);
    }

    loadModels();
  }, []);

  // Load persisted model id from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("selectedModel");
    if (stored) setModel(stored);
  }, []);

  // Load threshold from settings (refillBelow)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getSettings();
        if (!cancelled)
          setLowThreshold(Math.max(0, Number((s as any)?.refillBelow ?? 100)));
      } catch {
      } finally {
        if (!cancelled) setThresholdReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen to usage events and reflect totals for the active conversation
  useEffect(() => {
    const load = () => {
      try {
        // credit balance (system-wide via API)
        try {
          const uid =
            localStorage.getItem("user_email") ||
            localStorage.getItem("user_name") ||
            localStorage.getItem("auth_token") ||
            "guest";
          const hdrs: any = { "x-user-id": uid };
          try {
            const n = localStorage.getItem("user_name");
            const e = localStorage.getItem("user_email");
            if (n) hdrs["x-user-name"] = n;
            if (e) hdrs["x-user-email"] = e;
          } catch {}
          fetch("/api/credits/state", {
            cache: "no-store",
            headers: hdrs,
          })
            .then(async (r) => {
              if (!r.ok) {
                setCreditReady(true);
                return;
              }
              const d = await r.json();
              const bal = Number(d?.balance || 0);
              setCredit(Number.isFinite(bal) ? bal : 0);
              try {
                const n = (d?.name || '').trim();
                const e = (d?.email || '').trim();
                if (n) localStorage.setItem('user_name', n);
                if (e && !localStorage.getItem('user_email')) localStorage.setItem('user_email', e);
              } catch {}
              setCreditReady(true);
            })
            .catch(() => {
              setCreditReady(true);
            });
        } catch {
          setCreditReady(true);
        }
        const conv =
          localStorage.getItem("active_conversation_id") ||
          localStorage.getItem("active_session_id");
        if (conv) {
          setActiveConv(String(conv));
          const totals = JSON.parse(
            localStorage.getItem(`ai_usage_totals:${conv}`) || "null"
          );
          const last = JSON.parse(
            localStorage.getItem(`ai_usage_last:${conv}`) || "null"
          );
          if (totals && typeof totals === "object")
            setUsageTotals({
              prompt: Number(totals.prompt || 0),
              completion: Number(totals.completion || 0),
              total: Number(totals.total || 0),
            });
          else setUsageTotals(null);
          if (last && typeof last === "object")
            setUsageLast({
              prompt: Number(last.prompt || 0),
              completion: Number(last.completion || 0),
              total: Number(last.total || 0),
              at: Number(last.at || Date.now()),
            });
          else setUsageLast(null);
        }
      } catch {}
    };
    load();
    const onUsage = () => load();
    const onStorage = () => load();
    const onCredits = () => load();
    window.addEventListener("ai-usage" as any, onUsage);
    window.addEventListener("storage", onStorage);
    window.addEventListener("credits-update" as any, onCredits);
    return () => {
      window.removeEventListener("ai-usage" as any, onUsage);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("credits-update" as any, onCredits);
    };
  }, []);

  // Notify when credit is low or depleted
  useEffect(() => {
    if (!creditReady || !thresholdReady) return;
    try {
      let level: "none" | "warn" | "out" = "none";
      if (credit <= 0) level = "out";
      else if (credit <= Math.max(1, lowThreshold)) level = "warn";
      const prev = lastLevelRef.current || "none";
      if (prev !== level) {
        lastLevelRef.current = level;
        if (level === "out") {
          setSnackMsg("เครดิตหมด กรุณาเติมเครดิต");
          setSnackSeverity("error");
          setSnackOpen(true);
        } else if (level === "warn") {
          setSnackMsg(`เครดิตใกล้หมด เหลือ ${credit}`);
          setSnackSeverity("warning");
          setSnackOpen(true);
        }
      }
      if (level === "none" && snackOpen) setSnackOpen(false);
    } catch {}
  }, [credit, lowThreshold]);

  const handleSnackClose = (_?: any, reason?: string) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  // Update selection and broadcast change
  const handleChange = (e: SelectChangeEvent) => {
    const id = e.target.value as string;
    setModel(id);
    localStorage.setItem("selectedModel", id);
    window.dispatchEvent(new CustomEvent("modelChanged", { detail: id }));
  };

  // Load/display current user info from localStorage; prefer full name only
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("auth_token");
      let name = localStorage.getItem("user_name");
      let email = localStorage.getItem("user_email");
      const avatar = localStorage.getItem("user_avatar_url");

      // If logged in but no stored name, try to fetch from common profile endpoints
      if (token && !name) {
        const candidates = [
          "/auth/me",
          "/api/auth/me",
          "/me",
          "/api/me",
          "/profile",
          "/api/profile",
          "/users/me",
          "/api/users/me",
          "/auth/user",
          "/api/auth/user",
        ];
        for (const p of candidates) {
          try {
            const r = await fetch(`${API_BASE}${p}`, { credentials: "include" });
            if (!r.ok) continue;
            const d: any = await r.json();
            const first = d?.first_name || d?.firstName || d?.user?.first_name || d?.user?.firstName;
            const last = d?.last_name || d?.lastName || d?.user?.last_name || d?.user?.lastName;
            const composed = [first, last].filter(Boolean).join(' ').trim();
            const n =
              d?.name ||
              d?.full_name ||
              d?.fullName ||
              d?.fullname ||
              d?.user?.name ||
              d?.user?.full_name ||
              d?.user?.fullname ||
              (composed || undefined);
            const e = d?.email || d?.user?.email;
            const av = d?.avatar || d?.avatar_url || d?.avatarUrl || d?.image || d?.photo_url || d?.user?.avatar;
            if (typeof n === "string" && n.trim()) {
              name = n.trim();
              localStorage.setItem("user_name", name);
            }
            if (typeof e === "string" && e.trim() && !email) {
              email = e.trim();
              localStorage.setItem("user_email", email);
            }
            if (typeof av === "string" && av.trim()) {
              localStorage.setItem("user_avatar_url", av.trim());
              setAvatarUrl(av.trim());
            }
            if (name) break;
          } catch {}
        }
      }

      setLoggedIn(!!token);
      // Do not show email in navbar; show full name or fallback
      setDisplayName(name || null);
      setAvatarUrl(avatar || null);
    };
    loadUser();
    const onStorage = () => loadUser();
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const selected = useMemo(
    () => models.find((m) => m.id === model) || models[0],
    [model, models]
  );

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_id");
    setLoggedIn(false);
    setDisplayName(null);
    router.push("/login");
  };

  return (
    <>
      <header className="border-solid border-b-1 border-gray-400 sticky top-0 z-30 bg-gray-900/85 backdrop-blur supports-[backdrop-filter]:bg-gray-900/60">
        <div className="h-20 px-5 flex items-center justify-between">
          {/* Dropdown: model selector */}
          <FormControl
            size="small"
            variant="outlined"
            sx={{
              minWidth: 220,
              "& .MuiInputBase-root": {
                color: "#E5E7EB",
                backgroundColor: "#11182799",
                borderRadius: 8,
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#374151" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6B7280",
              },
              ".Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6366F1",
              },
              "& .MuiSvgIcon-root": { color: "#9CA3AF" },
            }}
          >
            <InputLabel sx={{ color: "#9CA3AF" }}>Model</InputLabel>
            <Select
              label="Model"
              value={model}
              onChange={handleChange}
              renderValue={() => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  {selected?.logo ? (
                    <Avatar
                      src={selected.logo}
                      sx={{ width: 28, height: 28 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1.5,
                        bgcolor: selected?.color,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {selected?.badge}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: 14, color: "#E5E7EB" }}>
                    {selected?.label}
                  </Typography>
                </Box>
              )}
            >
              {models.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {m.logo ? (
                      <Avatar src={m.logo} sx={{ width: 28, height: 28 }} />
                    ) : (
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          bgcolor: m.color,
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {m.badge}
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 14 }}>{m.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Indicators and actions */}
          <div className="flex items-center gap-3">
            <Tooltip title={`ยอดคงเหลือ: ${credit}`}>
              <Chip
                onClick={() => router.push("/admin/credits")}
                label={`Credit ${credit}`}
                size="small"
                sx={{
                  bgcolor: "#0b1220",
                  color: "#e5e7eb",
                  border: "1px solid #334155",
                }}
              />
            </Tooltip>
            {usageTotals ? (
              <Tooltip
                title={`โทเคนล่าสุด ${usageLast?.prompt || 0}/${
                  usageLast?.completion || 0
                } รวม ${usageLast?.total || 0}`}
              >
                <Chip
                  onClick={() => router.push("/chats/ai-info")}
                  label={`Tokens ${usageLast?.prompt}/${usageLast?.completion} (${usageLast?.total})`}
                  size="small"
                  sx={{
                    bgcolor: "#0b1220",
                    color: "#e5e7eb",
                    border: "1px solid #334155",
                  }}
                />
              </Tooltip>
            ) : null}
            {loggedIn ? (
              <>
                <div className="text-sm text-gray-300 hidden sm:block truncate max-w-[160px]">
                  {displayName || "User"}
                </div>
                <Tooltip title={displayName || "Account"}>
                  <IconButton size="small">
                    <Avatar src={avatarUrl || undefined} sx={{ width: 28, height: 28, bgcolor: "#4F46E5" }}>
                      {(displayName?.[0] || "U").toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                {/*  */}
              </>
            ) : (
              <Button
                size="small"
                variant="contained"
                onClick={() => router.push("/login")}
                sx={{
                  bgcolor: "#4F46E5",
                  textTransform: "none",
                  borderRadius: "9999px", // <-- เพิ่มบรรทัดนี้
                }}
              >
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snackSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
